// @ts-strict-ignore
import React, { memo, useMemo } from 'react'
import { Box, ChipList, ChipProps, NoResults, RowsSkeletonLoader } from '@node-space/ui-components'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { BeneficiaryDetails } from 'types/beneficiaries'
import { SourcePayWallet } from '../../Pay/types'
import BeneficiaryFormCallout from './BeneficiaryFormCallout'
import BeneficiarySearchList from './BeneficiarySearchList'

interface BeneficiaryContentProps {
  fiatBeneficiaries: BeneficiaryDetails[] | null
  filters: ChipProps[]
  isFetchingFilters: boolean
  isFetchingBeneficiaries: boolean
  isInsufficientBalance: boolean
  isPayoutsSupported: boolean
  sourceWalletAtom: SourcePayWallet
  handleBeneficiarySelect: (beneficiary: BeneficiaryDetails) => void
  handleBeneficiaryFilterChange: (selectedFilter: string[]) => void
  setSearchQuery: (searchQuery: string) => void
}

const BeneficiaryContent = ({
  fiatBeneficiaries,
  filters,
  isFetchingFilters,
  isFetchingBeneficiaries,
  isInsufficientBalance,
  isPayoutsSupported,
  sourceWalletAtom,
  handleBeneficiarySelect,
  handleBeneficiaryFilterChange,
  setSearchQuery,
}: BeneficiaryContentProps) => {
  const isLoadingListContent = useMemo(() => {
    return isFetchingFilters || isFetchingBeneficiaries
  }, [isFetchingFilters, isFetchingBeneficiaries, fiatBeneficiaries?.length])

  const showBeneficiaryList = useMemo(() => {
    return (
      !isLoadingListContent &&
      isPayoutsSupported &&
      !isInsufficientBalance &&
      !!fiatBeneficiaries?.length
    )
  }, [isPayoutsSupported, isInsufficientBalance, isLoadingListContent, fiatBeneficiaries])

  const showEmptyState = useMemo(() => {
    return (
      !isLoadingListContent &&
      isPayoutsSupported &&
      !isInsufficientBalance &&
      !!fiatBeneficiaries &&
      !fiatBeneficiaries?.length
    )
  }, [isPayoutsSupported, isInsufficientBalance, isLoadingListContent, fiatBeneficiaries])

  const showCallout = useMemo(() => {
    return !isLoadingListContent && (!isPayoutsSupported || isInsufficientBalance)
  }, [isPayoutsSupported, isInsufficientBalance, isLoadingListContent])

  return (
    <Box>
      {isFetchingFilters ? (
        <RowsSkeletonLoader rows={1} />
      ) : (
        filters?.length > 1 &&
        !showCallout && (
          <Box className="mb-4">
            <ChipList
              key={sourceWalletAtom?.walletId}
              multiselect={false}
              chips={filters}
              onHandleChange={handleBeneficiaryFilterChange}
            />
          </Box>
        )
      )}

      {isLoadingListContent && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

      {showBeneficiaryList && (
        <BeneficiarySearchList
          beneficiaries={fiatBeneficiaries}
          handleBeneficiarySelect={handleBeneficiarySelect}
        />
      )}

      {showEmptyState && (
        <NoResults
          onClear={() => {
            setSearchQuery(null)
          }}
        />
      )}

      {showCallout && (
        <BeneficiaryFormCallout
          key={sourceWalletAtom?.walletId}
          isInsufficientBalance={isInsufficientBalance}
          isPayoutsSupported={isPayoutsSupported}
          wallet={sourceWalletAtom}
        />
      )}
    </Box>
  )
}

export default memo(BeneficiaryContent)
